import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, User, LogOut, MessageSquare, Building2, Bell } from 'lucide-react';
import { useUser } from '../context/UserContext';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useUser();

    // Company account exclusion toggle
    const [excludeCompany, setExcludeCompany] = useState(() => {
        const saved = localStorage.getItem('excludeCompanyAccounts');
        return saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem('excludeCompanyAccounts', excludeCompany.toString());
    }, [excludeCompany]);

    // Logout handler
    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const navItems = [
        { name: 'Dashboard', path: '/', icon: LayoutDashboard },
        { name: 'Users', path: '/users', icon: Users },
        { name: 'Feedback', path: '/feedback', icon: MessageSquare },
        { name: 'Notice', path: '/notice', icon: Bell },
    ];

    return (
        <div className="flex h-screen bg-gray-100">
            {/* Sidebar */}
            <div className="w-64 bg-white shadow-lg flex flex-col">
                <div className="p-6 border-b">
                    <h1 className="text-2xl font-bold text-gray-800">PM AutoTwin</h1>
                </div>
                <nav className="flex-1 p-4 space-y-2">
                    {navItems.map((item) => (
                        <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === item.path
                                ? 'bg-blue-50 text-blue-600'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span className="font-medium">{item.name}</span>
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t space-y-2">
                    {/* Company Account Exclusion Toggle */}
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2 text-gray-600">
                            <Building2 className="w-4 h-4" />
                            <span className="text-sm">회사 계정 제외</span>
                        </div>
                        <button
                            onClick={() => setExcludeCompany(!excludeCompany)}
                            className={`relative w-10 h-5 rounded-full transition-colors ${excludeCompany ? 'bg-blue-500' : 'bg-gray-300'
                                }`}
                        >
                            <span
                                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${excludeCompany ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                            />
                        </button>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Logout</span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-auto">
                <header className="bg-white shadow-sm p-4 flex justify-between items-center sm:px-6 lg:px-8">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {navItems.find(i => i.path === location.pathname)?.name || 'Dashboard'}
                    </h2>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 text-gray-600">
                            <User className="w-5 h-5" />
                            <span className="text-sm font-medium">Administrator</span>
                        </div>
                    </div>
                </header>
                <main className="p-6 sm:px-8 lg:px-10">
                    <Outlet context={{ excludeCompany }} />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
